import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { EmployeeAvatar } from "@/components/org-chart/EmployeeAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader } from "@/components/ui/sidebar";
import { useAuth } from "@/hooks/use-auth";
import { useEmployeeAccess } from "@/hooks/use-employee-access";
import { useSurveyAccess } from "@/hooks/use-survey-access";
import { portalRpc } from "@/lib/portal-rpc";
import { orpc } from "@/lib/rpc";
import { cn } from "@/lib/utils";

type NavItem = {
  title: string;
  url: string;
  badge?: string;
};

type NavSection = {
  title: string;
  items: NavItem[];
};

const adminTopItemsBase: NavItem[] = [
  { title: "Dashboard", url: "/" },
  { title: "Handbook", url: "/company" },
  { title: "Minhas Entrevistas", url: "/my-interviews" },
  { title: "Minhas Pesquisas", url: "/my-surveys" },
];

const meuOnboardingItem: NavItem = { title: "Meu Onboarding", url: "/onboarding" };

const recruitmentOffersItem: NavItem = { title: "Ofertas", url: "/offers" };
const recruitmentTemplatesItem: NavItem = { title: "Templates de Oferta", url: "/offer-templates" };
const recruitmentBaseItems: NavItem[] = [
  { title: "Links de Agendamento", url: "/scheduling-links" },
];

const employeeTopItemsBase: NavItem[] = [
  { title: "Início", url: "/" },
  { title: "Handbook", url: "/company" },
  { title: "Organograma", url: "/org-chart" },
  { title: "Minhas Entrevistas", url: "/my-interviews" },
  { title: "Minhas Pesquisas", url: "/my-surveys" },
];

const labsSection: NavSection = {
  title: "Labs",
  items: [
    { title: "Inteligência de Candidatos", url: "/labs/candidate-intelligence", badge: "Alpha" },
    { title: "Avaliação de Nível", url: "/labs/level-assessment", badge: "Alpha" },
    { title: "Sourcing", url: "/labs/sourcing", badge: "Alpha" },
    { title: "Feedback Loop — Ofertas", url: "/labs/offer-recommendation-feedback", badge: "Beta" },
  ],
};

/**
 * Active state per DESIGN v7 §13: Sand Step bg + 2px coral left + body-strong.
 * Hover per Coral Boundary Rule: row bg does not change; word color shifts toward coral.
 */
function navRowClass(active: boolean, indent: "row" | "nested" = "row") {
  return cn(
    "flex items-center w-full text-[13px] rounded-md transition-colors",
    "border-l-2 -ml-px",
    indent === "row" ? "pl-3 pr-3 py-1.5" : "pl-6 pr-3 py-1",
    active
      ? "border-primary bg-sidebar-accent text-foreground font-semibold"
      : "border-transparent text-foreground/80 hover:text-primary",
  );
}

function NavLink({
  href,
  active,
  children,
  testId,
  indent = "row",
}: {
  href: string;
  active: boolean;
  children: React.ReactNode;
  testId?: string;
  indent?: "row" | "nested";
}) {
  return (
    <Link href={href} className={navRowClass(active, indent)} data-testid={testId}>
      <span className="flex items-center gap-2 truncate">{children}</span>
    </Link>
  );
}

function VagasNavItem({
  pinnedJobs,
  isActive,
}: {
  pinnedJobs: { id: string; title: string; jobLevel: string }[];
  isActive: (url: string) => boolean;
}) {
  const isJobsActive = isActive("/jobs");
  const activePinnedJob = pinnedJobs.some((j) => isActive(`/jobs/${j.id}`));

  return (
    <>
      <NavLink href="/jobs" active={isJobsActive && !activePinnedJob} testId="nav-vagas">
        Vagas
      </NavLink>
      {pinnedJobs.map((job) => (
        <NavLink
          key={job.id}
          href={`/jobs/${job.id}`}
          active={isActive(`/jobs/${job.id}`)}
          testId={`nav-pinned-job-${job.id}`}
          indent="nested"
        >
          <span className="truncate text-xs">
            {job.title} — {job.jobLevel}
          </span>
        </NavLink>
      ))}
    </>
  );
}

/**
 * Section group — caption-mono uppercase label + hairline above (except first).
 * Always open; no chevron, no icon (DESIGN v7 Blocker #2).
 */
function NavSectionBlock({
  title,
  isFirst,
  isActive,
  items,
  prependChildren,
}: {
  title: string;
  isFirst?: boolean;
  isActive: (url: string) => boolean;
  items: NavItem[];
  prependChildren?: React.ReactNode;
}) {
  return (
    <div className={cn("pt-3", !isFirst && "mt-3 border-t border-border")}>
      <div
        className="caption-mono px-3 pb-2"
        data-testid={`nav-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {title}
      </div>
      <div className="flex flex-col gap-0.5">
        {prependChildren}
        {items.map((item) => (
          <NavLink
            key={item.title}
            href={item.url}
            active={isActive(item.url)}
            testId={`nav-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate">{item.title}</span>
              {item.badge && (
                <span className="caption-mono shrink-0 text-[10px] text-muted-foreground">
                  {item.badge}
                </span>
              )}
            </span>
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export function AppSidebar() {
  const [location, navigate] = useLocation();
  const {
    user,
    employee,
    isSuperAdmin,
    isManager,
    isBuddyOfActiveOnboarding,
    hasPermission,
    logout,
  } = useAuth();
  const { canOperateSurveys } = useSurveyAccess();

  const { data: demoUrlData } = useQuery(orpc.settings.getDemoUrl.queryOptions());
  const { data: pinnedJobs = [] } = useQuery(orpc.jobFavorites.listPinned.queryOptions());

  const isActive = (url: string) => {
    if (url === "/") return location === "/";
    if (url === "/onboarding") {
      return location === "/onboarding" || location.startsWith("/onboarding/");
    }
    return location === url || location.startsWith(`${url}/`);
  };

  const portalToday = useQuery({
    ...portalRpc.getToday.queryOptions(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const hasActiveOnboarding = portalToday.data?.ok === true;

  const adminTopItems: NavItem[] = hasActiveOnboarding
    ? [adminTopItemsBase[0], meuOnboardingItem, ...adminTopItemsBase.slice(1)]
    : adminTopItemsBase;

  const employeeTopItems: NavItem[] = hasActiveOnboarding
    ? [employeeTopItemsBase[0], meuOnboardingItem, ...employeeTopItemsBase.slice(1)]
    : employeeTopItemsBase;

  const topItems = isSuperAdmin ? adminTopItems : employeeTopItems;
  const showBuddyOnboarding = !isSuperAdmin && !isManager && isBuddyOfActiveOnboarding;

  // Manager stays as a dynamic bypass alongside hasPermission — managers don't
  // get rows in user_permissions; if someone stops being a manager, access ends.
  const { canMaintainEmployeeBase } = useEmployeeAccess();
  const canSeeEmployees = isManager || canMaintainEmployeeBase;
  const canSeeOrgChart = hasPermission("org_chart");
  const canSeeOnboarding = isManager || hasPermission("onboarding");
  const canSeePerformance = hasPermission("performance");
  // "Pesquisas de Clima" opens for the Admin de Pesquisas and for the Operador
  // de Pesquisas alike — what differs inside the page is whether the answers
  // are readable, which the server decides per payload. Must move together with
  // the route gate in App.tsx, or this link 404s.
  const canSeeSurveysFull = canOperateSurveys;
  const canSeeSalaryTables = isManager || hasPermission("salary_tables");
  const canSeeRecruiting = hasPermission("recruiting");

  // Candidate Intelligence and Level Assessment are open to every authenticated
  // platform user (docs/labs/candidate-intelligence/access-model.md,
  // docs/labs/level-assessment/access-model.md); the other labs tools (sourcing,
  // offer-feedback) stay gated behind the labs grant. So the Labs section always
  // shows those two, and the rest only when the user holds a labs permission.
  const hasFullLabsNav = hasPermission("labs_intel") || hasPermission("labs_feedback");
  const alwaysOpenLabsUrls = new Set(["/labs/candidate-intelligence", "/labs/level-assessment"]);
  const labsItems = hasFullLabsNav
    ? labsSection.items
    : labsSection.items.filter((item) => alwaysOpenLabsUrls.has(item.url));

  const gestaoSection: NavSection = {
    title: "Gestão",
    items: [
      ...(canSeeEmployees ? [{ title: "Colaboradores", url: "/employees" }] : []),
      ...(canSeeOrgChart ? [{ title: "Organograma", url: "/org-chart" }] : []),
      ...(canSeeOnboarding ? [{ title: "Onboarding", url: "/onboardings" }] : []),
      ...(canSeePerformance ? [{ title: "Desempenho", url: "/performance" }] : []),
      ...(canSeeSurveysFull
        ? [{ title: "Pesquisas de Clima", url: "/surveys" }]
        : isManager
          ? [{ title: "Pesquisas do meu time", url: "/surveys" }]
          : []),
      ...(canSeeSalaryTables ? [{ title: "Tabelas Salariais", url: "/salary-tables" }] : []),
    ],
  };

  const recruitmentSection: NavSection = {
    title: "Recrutamento",
    items: [
      ...(canSeeRecruiting ? [recruitmentOffersItem] : []),
      ...(canSeeRecruiting || isManager ? [recruitmentTemplatesItem] : []),
      ...recruitmentBaseItems,
    ],
  };

  const displayName = employee?.fullName || user?.email || "Conta";
  const roleLabel = isSuperAdmin ? "Administrador" : isManager ? "Gestor" : "Colaborador";
  const userEmail = user?.email ?? "";

  return (
    <Sidebar>
      {/* DESIGN.md §10.2 v2: logo block at top of sidebar. Comp wordmark
          replaces the "Comp HR" label (wordmark já carrega o nome). */}
      <SidebarHeader className="h-[52px] flex-row items-center border-b border-sidebar-border px-4 py-0">
        <img
          src="/logo-comp.png"
          alt="Comp"
          className="h-[20px] w-auto shrink-0 select-none"
          draggable={false}
        />
      </SidebarHeader>

      <SidebarContent className="px-2 py-3 gap-0">
        {/* Top items — flat, no group label */}
        <div className="flex flex-col gap-0.5">
          {topItems.map((item) => (
            <NavLink
              key={item.title}
              href={item.url}
              active={isActive(item.url)}
              testId={`nav-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {item.title}
            </NavLink>
          ))}
        </div>

        {/* Sections — visible when they have items for the current role */}
        {gestaoSection.items.length > 0 && (
          <NavSectionBlock
            title={gestaoSection.title}
            isActive={isActive}
            items={gestaoSection.items}
          />
        )}
        <NavSectionBlock
          title={recruitmentSection.title}
          isActive={isActive}
          items={recruitmentSection.items}
          prependChildren={<VagasNavItem pinnedJobs={pinnedJobs} isActive={isActive} />}
        />
        {labsItems.length > 0 && (
          <NavSectionBlock title={labsSection.title} isActive={isActive} items={labsItems} />
        )}

        {showBuddyOnboarding && (
          <div className="mt-3 border-t border-border pt-3">
            <div className="flex flex-col gap-0.5">
              <NavLink
                href="/onboardings"
                active={isActive("/onboardings")}
                testId="nav-onboarding-buddy"
              >
                Onboarding
              </NavLink>
            </div>
          </div>
        )}
      </SidebarContent>

      {/* User menu — moved to footer per DESIGN.md §10.2 v2 */}
      <SidebarFooter className="border-t border-sidebar-border px-3 py-2.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center gap-2.5 rounded-md p-1 text-left outline-none transition-colors hover:bg-[hsl(var(--sand-step))]"
              data-testid="button-user-menu"
            >
              <EmployeeAvatar fullName={displayName} photoUrl={employee?.slackPhotoUrl} size={26} />
              <div className="flex min-w-0 flex-1 flex-col leading-tight">
                <span className="truncate text-[12.5px] font-semibold text-foreground">
                  {displayName}
                </span>
                <span className="truncate text-[11px] text-muted-foreground">
                  {roleLabel}
                  {userEmail && ` · ${userEmail}`}
                </span>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56" side="top" sideOffset={4}>
            <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
              Perfil
            </DropdownMenuItem>
            {demoUrlData?.url && (
              <DropdownMenuItem
                onClick={() => window.open(demoUrlData.url, "_blank", "noopener,noreferrer")}
                data-testid="menu-demo"
              >
                Demo
              </DropdownMenuItem>
            )}
            {isSuperAdmin && (
              <DropdownMenuItem
                onClick={() => navigate("/backoffice")}
                data-testid="menu-backoffice"
              >
                Backoffice
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="menu-logout">
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
